import { useState } from 'react';
import type { Action, Billet, Commande } from '../../api/types';
import { formatDate } from '../../lib/format';
import { Button, Section } from '../ui';
import DateDelivranceModal from './DateDelivranceModal';

const FOUR_HOURS_MS = 4 * 3600_000;

type Etat = 'delivre' | 'differe' | 'a-delivrer';

/** Un QR est délivré quand `numeroCodeBarre` est non vide ("" = retiré après un transfert annulé). */
export function etatQrCodes(commande: Commande, billets: Billet[]): Etat {
  const tousDelivres = billets.length > 0 && billets.every((b) => Boolean(b.numeroCodeBarre?.trim()));
  if (tousDelivres) return 'delivre';
  return commande.envoiCodeBarreDiffere ? 'differe' : 'a-delivrer';
}

function dateDevoilage(commande: Commande): { date: Date; parDefaut: boolean } {
  if (commande.dateAffichageCodeBarre) return { date: new Date(commande.dateAffichageCodeBarre), parDefaut: false };
  return { date: new Date(new Date(commande.dateSeance).getTime() - FOUR_HOURS_MS), parDefaut: true };
}

/**
 * Gestion des QR codes, pensée comme un parcours :
 *  - à délivrer  → « Délivrer maintenant » ou « Délivrer en différé » (modale : calendrier + H-4/H-24/H-48) ;
 *  - en différé  → rappel de la date, modifier (même modale), délivrer tout de suite, annuler le différé ;
 *  - délivrés    → retrait des QR codes.
 * Comme chez Trium (guide §7.2), en différé les QR ne sont PAS envoyés : à la date, il faut les délivrer.
 * Toutes ces actions lèvent `actionCodeBarre` (l'app recharge la commande et ses QR).
 */
export default function GestionQrCodes({
  commande,
  billets,
  now,
  onActions,
  busy,
}: {
  commande: Commande;
  billets: Billet[];
  now: Date;
  /** Exécute les actions dans l'ordre (une seule notification à la fin). */
  onActions: (actions: Action[], message: string) => void;
  busy: boolean;
}) {
  const etat = etatQrCodes(commande, billets);
  const { date, parDefaut } = dateDevoilage(commande);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const delivres = billets.filter((b) => b.numeroCodeBarre?.trim()).length;

  const programmer = (dateChoisie: string | null) => {
    setModaleOuverte(false);
    onActions(
      [
        ...(commande.envoiCodeBarreDiffere ? [] : [{ action: 'setDeferred', body: { envoiCodeBarreDiffere: true } } as Action]),
        { action: 'setDisplayDate', body: { date: dateChoisie } },
      ],
      commande.envoiCodeBarreDiffere ? 'Date de délivrance modifiée' : 'Délivrance différée programmée',
    );
  };

  return (
    <Section title="Gestion des QR codes">
      {etat === 'a-delivrer' ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => onActions([{ action: 'deliverBarcodes', body: {} }], 'QR codes délivrés')}
          >
            Délivrer maintenant
          </Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setModaleOuverte(true)}>
            Délivrer en différé
          </Button>
        </div>
      ) : null}

      {etat === 'differe' ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-card-border bg-surface-inset p-3">
            <p className="text-sm text-on-surface-variant">
              Délivrance prévue le <span className="font-semibold text-white">{formatDate(date.toISOString())}</span>
              {parDefaut ? ' (4 h avant la séance)' : ''}
              {date.getTime() <= now.getTime() ? (
                <span className="ml-2 font-semibold text-jupiter">— date passée, à délivrer</span>
              ) : null}
            </p>
            <p className="mt-1 text-xs/4 text-outline">
              Comme Trium, le BO n’envoie pas les QR automatiquement : à la date, cliquer « Délivrer tout de suite ».
              Pour tester sans attendre, décaler l’horloge serveur.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() =>
                onActions(
                  [
                    { action: 'setDeferred', body: { envoiCodeBarreDiffere: false } },
                    { action: 'deliverBarcodes', body: {} },
                  ],
                  'QR codes délivrés',
                )
              }
            >
              Délivrer tout de suite
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setModaleOuverte(true)}>
              Modifier la date
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() =>
                onActions(
                  [
                    { action: 'setDeferred', body: { envoiCodeBarreDiffere: false } },
                    { action: 'setDisplayDate', body: { date: null } },
                  ],
                  'Délivrance différée annulée',
                )
              }
            >
              Annuler le différé
            </Button>
          </div>
        </div>
      ) : null}

      {etat === 'delivre' ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-card-border bg-surface-inset p-3">
          <div>
            <p className="font-semibold text-white">
              QR codes délivrés ({delivres}/{billets.length})
            </p>
            {commande.envoiCodeBarreDiffere && date.getTime() > now.getTime() ? (
              <p className="text-xs/4 text-outline">
                Mode différé : visibles dans l’app à partir du {formatDate(date.toISOString())}.
              </p>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => onActions([{ action: 'withdrawBarcodes', body: {} }], 'QR codes retirés')}
          >
            Retirer les QR codes
          </Button>
        </div>
      ) : null}

      {modaleOuverte ? (
        <DateDelivranceModal
          open
          dateSeance={commande.dateSeance}
          initial={commande.dateAffichageCodeBarre}
          today={now}
          busy={busy}
          onClose={() => setModaleOuverte(false)}
          onValidate={programmer}
        />
      ) : null}
    </Section>
  );
}
